import { useEffect, useCallback, useRef } from "react";
import "./RoleName.css";
import "../OptionBlocks.css";
import {
  JustifiedAlignment3,
  LeftAlignment2,
  RaggedAlignment,
  RightAlignment2,
  DoubleColumn,
  SingleColumn,
  RoleFirst,
  NamesFirst,
  SideAB,
  InsetOff,
  InsetLeft,
  InsetRight,
  gutterJustify,
  SingleColumnCenter,
} from "../../../../../assets/icons/index";

import {
  NumberInput,
  Radio,
  NumberInput_fontsize,
} from "../../../../../Components";

import {
  updateProjectCreditOptionsState,
  saveOptionById,
  updateLayoutFontStyleState,
  saveFontStyle,
} from "../../../../../Redux/actions/projectActions";

import { LineHeight } from "../../../../ProjectV2/Options/OptionFont/Functions";
import HeadingDisclosure from "../HeadingDisclosure";
import LayoutDisclosure from "../LayoutDisclosure";
import ColumnWidthPresets from "../ColumnWidthPresets";

import { useDispatch, useSelector } from "react-redux";
import {
  maxColumnWidthPct,
  clampRoleNameColumnWidthPct,
  roleNameColumnWidthMin,
} from "../../../utils/maxColumnWidth";
import { scheduleProjectSave } from "../../../projectSaveCoordinator";

const RoleName = ({
  options,
  currentBlockIdx,
  currentBlockId,
  isCard,
}) => {
  const dispatch = useDispatch();

  // GET CURRENT BLOCK
  const {
    credits,
    currentBlock,
    _id: projectId,
    fontStyle: projectFontStyle,
  } = useSelector((state) => state?.project ?? {});

  const currentBlockData = credits?.find(
    (block, index) =>
      index === currentBlock?.idx &&
      block?._id === currentBlock?.id
  );

  const currentBlockType = currentBlockData?.options?.block_type || options?.block_type || "rolename";
  const fontStyle = useSelector((state) => state?.project?.fontStyle?.name ?? {});
  const fontStyleType =  currentBlockType === "nameonly"  ? "nameOnlyStyle" : "name";
  const fontStyleTypeRef = useRef(fontStyleType);

  useEffect(() => {
    fontStyleTypeRef.current = fontStyleType;
  }, [fontStyleType]);

  const debouncedSave = useCallback((key, value, targetType) => {
      const saveType =  targetType || fontStyleType;
      const fontStyleSnapshot = {
        ...(projectFontStyle?.[saveType] || {}),
        [key]: value,
      };
      return scheduleProjectSave(
        `font-style-debounce:${projectId}:${saveType}`,
        async () => {
          try {
            await dispatch(
              saveFontStyle(
                saveType,
                currentBlockType,
                key,
                value,
                null,
                isCard,
                {
                  projectId,
                  isCard,
                  fontStyle: fontStyleSnapshot,
                  optimistic: true,
                },
              ),
            );
          } catch (error) {
            console.error("Failed to save font style:", error);
            throw error;
          }
        },
        1000,
      );
    }, [
      dispatch,
      fontStyleType,
      currentBlockType,
      isCard,
      projectId,
      projectFontStyle,
    ]
  );

  const handleChange = (key, value) => {
    dispatch(
      updateProjectCreditOptionsState(currentBlockIdx, key, value, true),
    );
  };

  const handleGapChange = (val) => {
    const num = Number(val);
    if (Number.isNaN(num)) return;
    const clampedValue = Math.min(Math.max(num, 0), 30);
    dispatch(
      updateLayoutFontStyleState(
        currentBlockId,
        currentBlockIdx,
        [
          {
            styleKey: "name",
            property: "marginBottom",
            value: clampedValue,
          },
          {
            styleKey: "role",
            property: "marginBottom",
            value: clampedValue,
          },
        ],
        "credit-height",
      ),
    );
    debouncedSave("marginBottom", clampedValue, "name");
    debouncedSave("marginBottom", clampedValue, "role");
  };

  const columnWidthMax = maxColumnWidthPct(options?.columns);
  const columnWidthMin = roleNameColumnWidthMin({
    doubleColumns: options?.double_columns,
    columns: options?.columns,
    blockInset: options?.block_inset,
  });

  const constrainColumnWidth = (value, overrides = {}) =>
    clampRoleNameColumnWidthPct(value, {
      ...options,
      ...overrides,
    });

  const constrainCurrentColumnWidth = (overrides) => {
    const constrainedWidth = constrainColumnWidth(
      options?.column_width,
      overrides,
    );
    if (constrainedWidth !== Number(options?.column_width)) {
      handleChange("column_width", constrainedWidth);
    }
  };

  const saveOptions = () => {
    dispatch(saveOptionById(currentBlockId, isCard));
  };

  useEffect(() => {
    if (columnWidthMin === 50 && Number(options?.column_width) !== 50) {
      dispatch(
        updateProjectCreditOptionsState(
          currentBlockIdx,
          "column_width",
          50,
          true,
        ),
      );
      dispatch(saveOptionById(currentBlockId, isCard));
    }
  }, [
    currentBlockId,
    currentBlockIdx,
    dispatch,
    isCard,
    columnWidthMin,
    options?.column_width,
  ]);

  const getValue = (value) => {
    return value ?? "";
  };

  return (
    <div className="Roles-name">
      <div className="rolename-inside">
        <HeadingDisclosure
          heading={getValue(options?.heading)}
          subheading={getValue(options?.subheading)}
          onChange={handleChange}
          onFocusOut={saveOptions}
        />

        <LayoutDisclosure title="Structure">
        <div className="optionSection">
          <div className="optionSectionLabel  inline-block group  cursor-help underline decoration-dashed decoration-[rgba(255,255,255,0.4)] underline-offset-4">
            <span className="cursor-help">Alignment</span>
            <span className="absolute hidden group-hover:block cursor-help ml-[-210px] mt-[-30px]  p-2 rounded-xl bg-[#191824] text-[#eee9ff] border-[1px] border-[rgba(255,255,255,0.1)] text-xs shadow-xl w-[200px] max-w-[200px] text-center">
              Choose between a single column or a double column layout.
            </span>
          </div>
          <div className="optionSectionContent">
            <Radio
              options={[
                { icon: DoubleColumn, value: true },
                { icon: SingleColumn, value: false },
              ]}
              value={options?.double_columns}
              onChange={(val) => {
                handleChange("double_columns", val);
                constrainCurrentColumnWidth({ double_columns: val });
                // Persist alignment fallbacks when switching single ↔ double.
                // Double: center → gutter. Single: gutter/ragged/justify → center.
                if (val) {
                  if (
                    !options?.text_alignment ||
                    options?.text_alignment === "center"
                  ) {
                    handleChange("text_alignment", "gutter");
                  }
                } else if (
                  options?.text_alignment === "gutter" ||
                  options?.text_alignment === "ragged" ||
                  options?.text_alignment === "justify"
                ) {
                  handleChange("text_alignment", "center");
                }
                saveOptions();
              }}
              label={options?.double_columns ? "Double Column" : "Single Column"}
              withIcon
            />
          </div>
        </div>

        <div className="optionSection">
          <div className="optionSectionLabel inline-block group cursor-help underline decoration-dashed decoration-[rgba(255,255,255,0.4)] underline-offset-4">
            <span className="cursor-help">Text Alignment</span>
            <span className="absolute hidden group-hover:block cursor-help ml-[-210px] mt-[-30px]  p-2 rounded-xl bg-[#191824] text-[#eee9ff] border-[1px] border-[rgba(255,255,255,0.1)] text-xs shadow-xl w-[200px] max-w-[200px] text-center">
              Adjust the visual spacing and dividing lines between roles and
              names.
            </span>
          </div>
          <div className="optionSectionContent">
            <Radio
              options={[
                {
                  icon: options?.double_columns
                    ? gutterJustify
                    : SingleColumnCenter,
                  value: options?.double_columns ? "gutter" : "center",
                },
                {
                  icon: LeftAlignment2,
                  value: "left",
                  // disabled: !options.double_columns,
                },

                {
                  icon: RightAlignment2,
                  value: "right",
                  // disabled: !options.double_columns,
                },
                {
                  icon: JustifiedAlignment3,
                  value: "justify",
                  disabled: !options?.double_columns,
                },
                {
                  icon: RaggedAlignment,
                  value: "ragged",
                  disabled: !options?.double_columns,
                },
              ]}
              value={
                options?.double_columns && options?.text_alignment === "center"
                  ? "gutter"
                  : options?.text_alignment
              }
              onChange={(val) => {
                handleChange("text_alignment", val);
                saveOptions();
              }}
              label={
                options?.double_columns && options?.text_alignment === "center"
                  ? "Gutter"
                  : !options?.double_columns
                    ? options?.text_alignment === "center"
                      ? "Center"
                      : options?.text_alignment === "left"
                        ? "Left"
                        : options?.text_alignment === "right"
                          ? "Right"
                          : "Center"
                    : options?.text_alignment
              }
              withIcon
            />
          </div>
        </div>

        <div className="optionSection ">
          <div className="optionSectionLabel inline-block group cursor-help underline decoration-dashed decoration-[rgba(255,255,255,0.4)] underline-offset-4">
            <span className="cursor-help">Arrangement</span>
            <span className="absolute hidden group-hover:block cursor-help ml-[-210px] mt-[-30px]  p-2 rounded-xl bg-[#191824] text-[#eee9ff] border-[1px] border-[rgba(255,255,255,0.1)] text-xs shadow-xl w-[200px] max-w-[200px] text-center">
              Set the number of columns to arrange the credits in.
            </span>
          </div>

          <div className="optionSectionContent">
            <NumberInput
              label="Column(s)"
              postLabel=""
              value={options?.columns}
              onChange={(val) => {
                handleChange("columns", val);
                constrainCurrentColumnWidth({ columns: val });
              }}
              min={1}
              max={2}
              onFocusOut={saveOptions}
              disabled={options?.bullets !== null}
              // customBtnMargin={{ marginLeft: "68px" }}
            />
          </div>
        </div>
        <div className="optionSection">
          <div className="optionSectionLabel inline-block group cursor-help underline decoration-dashed decoration-[rgba(255,255,255,0.4)] underline-offset-4">
            <span className="cursor-help">Order</span>
            <span className="absolute hidden group-hover:block cursor-help ml-[-210px] mt-[-30px]  p-2 rounded-xl bg-[#191824] text-[#eee9ff] border-[1px] border-[rgba(255,255,255,0.1)] text-xs shadow-xl w-[200px] max-w-[200px] text-center">
              Choose whether to display roles or names first..
            </span>
          </div>
          <div className="optionSectionContent">
            <Radio
              options={[
                { icon: RoleFirst, value: false },
                { icon: NamesFirst, value: true },
              ]}
              value={options?.name_on_left}
              onChange={(val) => {
                handleChange("name_on_left", val);
                saveOptions();
              }}
              label={options?.name_on_left ? "Names First" : "Roles First"}
              withIcon
            />
          </div>
        </div>

        {/* <hr className="border-b-2 border-[#e0e0e0]" /> */}

        <div className="optionSection">
          <div className="optionSectionLabel inline-block group cursor-help underline decoration-dashed decoration-[rgba(255,255,255,0.4)] underline-offset-4">
            <span className="cursor-help">Block Inset</span>
            <span className="absolute hidden group-hover:block cursor-help ml-[-210px] mt-[-30px]  p-2 rounded-xl bg-[#191824] text-[#eee9ff] border-[1px] border-[rgba(255,255,255,0.1)] text-xs shadow-xl w-[200px] max-w-[200px] text-center">
              Adjust the horizontal indentation and padding of the block.
            </span>
          </div>
          <div className="optionSectionContent block-option-content BLock-inset ">
            <Radio
              label={options?.block_inset}
              options={[
                {
                  icon: InsetOff,
                  value: "default",
                  disabled:
                    options?.side_by_side === "on" &&
                    (options?.block_inset === "default" ||
                      options?.block_inset === "left" ||
                      options?.block_inset === "right"),
                },
                {
                  icon: InsetLeft,
                  value: "left",
                  disabled: options?.side_by_side === "on",
                },
                {
                  icon: InsetRight,
                  value: "right",
                  disabled: options?.side_by_side === "on",
                },
                {
                  icon: SideAB,
                  value: "sidebyside",
                  disabled: options?.side_by_side === "on",
                },
              ]}
              value={options?.block_inset}
              onChange={(val) => {
                handleChange("block_inset", val);
                constrainCurrentColumnWidth({ block_inset: val });
                saveOptions();
              }}
              withIcon
            />
          </div>
        </div>
        </LayoutDisclosure>

        <LayoutDisclosure title="Spacing">
        <div className="optionSection">
          <div className="optionSectionLabel inline-block group cursor-help underline decoration-dashed decoration-[rgba(255,255,255,0.4)] underline-offset-4">
            <span className="cursor-help">Width</span>
            <span className="absolute hidden group-hover:block cursor-help ml-[-210px] mt-[-30px]  p-2 rounded-xl bg-[#191824] text-[#eee9ff] border-[1px] border-[rgba(255,255,255,0.1)] text-xs shadow-xl w-[200px] max-w-[200px] text-center">
              Adjust the horizontal width of the credit block as a percentage.
            </span>
          </div>

          <div className="optionSectionContent columnWidthControls">
            <ColumnWidthPresets
              value={constrainColumnWidth(options?.column_width)}
              minWidth={columnWidthMin}
              maxWidth={columnWidthMax}
              onChange={(value) => {
                handleChange("column_width", value);
                saveOptions();
              }}
            />
            <NumberInput
              postLabel="%"
              value={constrainColumnWidth(options?.column_width)}
              onChange={(val) =>
                handleChange("column_width", constrainColumnWidth(val))
              }
              min={columnWidthMin}
              max={columnWidthMax}
              onFocusOut={saveOptions}
            />
          </div>
        </div>
        <div className="optionSection">
          <div className="optionSectionLabel inline-block group cursor-help underline decoration-dashed decoration-[rgba(255,255,255,0.4)] underline-offset-4">
            <span className="cursor-help">Line Height</span>
            <span className="absolute hidden group-hover:block cursor-help ml-[-210px] mt-[-30px]  p-2 rounded-xl bg-[#191824] text-[#eee9ff] border-[1px] border-[rgba(255,255,255,0.1)] text-xs shadow-xl w-[200px] max-w-[200px] text-center">
              Adjust the vertical spacing of role and name text together.
            </span>
          </div>
          <div className="optionSectionContent">
            <LineHeight
              styleKey="role"
              syncKeys={["name"]}
              currentBlockType={currentBlockType}
              currentBlockId={currentBlockId}
              currentBlockIdx={currentBlockIdx}
              isCard={isCard}
            />
          </div>
        </div>
        <div className="optionSection ">
            <div className="optionSectionLabel inline-block group cursor-help underline decoration-dashed decoration-[rgba(255,255,255,0.4)] underline-offset-4">
              <span className="cursor-help">Credit Height</span>
              <span className="absolute hidden group-hover:block cursor-help ml-[-210px] mt-[-30px]  p-2 rounded-xl bg-[#191824] text-[#eee9ff] border-[1px] border-[rgba(255,255,255,0.1)] text-xs shadow-xl w-[200px] max-w-[200px] text-center">
                Add space between after every pair of Role-Name.
              </span>
            </div>

            <div className="optionSectionContent">
              
               <NumberInput_fontsize
                  label="Row"
                  step="1"
                  min={0}
                  max={30}
                  value={fontStyle?.marginBottom ?? 0}
                  onChange={handleGapChange}
                  inputBox
                  customWidth={"max-w-[100px]"}
                />
            </div>
          </div>

        {!isCard && (
          <div>
            <div className="optionSection">
              <div className="optionSectionLabel inline-block group cursor-help underline decoration-dashed decoration-[rgba(255,255,255,0.4)] underline-offset-4">
                <span className="cursor-help">Margin</span>
                <span className="absolute hidden group-hover:block cursor-help ml-[-210px] mt-[-30px]  p-2 rounded-xl bg-[#191824] text-[#eee9ff] border-[1px] border-[rgba(255,255,255,0.1)] text-xs shadow-xl w-[200px] max-w-[200px] text-center">
                  Adjust the vertical margin around the credit block.
                </span>
              </div>
              <div className="optionSectionContent block-option-content">
                <div className="label-2 label-2-roleName1440">Vertical</div>
                <NumberInput
                  value={options?.margin_vertical ?? 0}
                  min={0}
                  max={50}
                  step={10}
                  postLabel="%"
                  onChange={(val) => handleChange("margin_vertical", val)}
                  onFocusOut={saveOptions}
                />
              </div>
            </div>

            <div className="optionSection">
              <div className="optionSectionContent2 block-option-content">
                <div className="optionSectionLabel inline-block group cursor-help underline decoration-dashed decoration-[rgba(255,255,255,0.4)] underline-offset-4">
                  <span className="cursor-help">Bottom</span>
                  <span className="absolute hidden group-hover:block cursor-help ml-[-210px] mt-[-30px]  p-2 rounded-xl bg-[#191824] text-[#eee9ff] border-[1px] border-[rgba(255,255,255,0.1)] text-xs shadow-xl w-[200px] max-w-[200px] text-center">
                    Adjust the extra spacing directly below the credit block.
                  </span>
                </div>
                <NumberInput
                  label={<><span className="bg-gray" style={{color: "#fff", fontSize: "18px", marginRight: "8px", }}>&darr;</span></>}
                  value={options?.extra_lines_below}
                  min={0}
                  max={25}
                  onChange={(val) => handleChange("extra_lines_below", val)}
                  onFocusOut={saveOptions}
                />
              </div>
            </div>
          </div>
        )}
        </LayoutDisclosure>
      </div>
    </div>
  );
};

export default RoleName;
